// Client-side PDF text extraction using pdf.js.
// Running in the browser means the PDF bytes never leave the user's device
// except to Supabase Storage (encrypted at rest). Extracted text is what we
// send to the server for pseudonymization + AI review.

import * as pdfjsLib from 'pdfjs-dist'
// The bundled worker has to be registered so pdf.js can spawn a worker thread.
// Vite's ?url import gives us a hashed URL for the shipped worker bundle.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl

// Extracts plain text from a PDF file (File or Blob). Returns a single string
// with pages separated by double newlines. Page numbers are NOT included in
// the output; we annotate them inline so the AI can refer to "page N" if
// asked, but they stay stripped during pseudonymization.
export async function extractTextFromPdf(file) {
  const arrayBuffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise

  const pages = []
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const content = await page.getTextContent()
    const pageText = content.items
      .map(item => ('str' in item ? item.str : ''))
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim()
    if (pageText) pages.push(`[page ${i}]\n${pageText}`)
  }

  return {
    text: pages.join('\n\n'),
    pageCount: pdf.numPages,
    byteLength: arrayBuffer.byteLength
  }
}

// Downloads a PDF from Supabase Storage via signed URL, then extracts text.
// Used by the review flow when the user runs a review on a policy they
// uploaded previously (no re-pick from disk).
export async function extractTextFromStoragePath(supabase, bucket, filePath) {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(filePath, 60)
  if (error) throw error

  const response = await fetch(data.signedUrl)
  if (!response.ok) throw new Error(`Failed to fetch PDF (${response.status})`)
  const blob = await response.blob()
  return await extractTextFromPdf(blob)
}
